export function buildSpendComparison(config: any, records: any[]) {
  if (!Array.isArray(records) || !records.length || config.series?.length !== 2) throw new Error('Invalid comparison data');
  const rows = config.categories.map((category: any) => {
    const matching = records.filter(record => category.values.includes(String(record[config.dataSource.fields.category])));
    if (!matching.length) throw new Error('Missing category');
    const amounts = config.series.map((series: any) => matching.reduce((sum: number, record: any) => {
      const raw = record[config.dataSource.fields[series.id]];
      if (raw == null || String(raw).trim() === '' || !Number.isFinite(Number(raw)) || Number(raw) < 0) throw new Error('Invalid spend');
      return sum + Number(raw);
    }, 0));
    return { ...category, amounts };
  });
  const totals = config.series.map((_: any, index: number) => rows.reduce((sum: number, row: any) => sum + row.amounts[index], 0));
  const total = { id: 'total', label: config.labels.total, amounts: totals };
  const displayRows = config.comparison.showTotal ? [...rows, total] : rows;
  const format = (value: number) => new Intl.NumberFormat(config.numberFormat.locale, {
    minimumFractionDigits: config.numberFormat.decimals, maximumFractionDigits: config.numberFormat.decimals,
  }).format(value);
  const money = (value: number) => `${value < 0 ? '-' : ''}${config.numberFormat.prefix || ''}${format(Math.abs(value))}${config.numberFormat.suffix || ''}`;
  const options = config.chartOptions || {};
  return {
    rows, displayRows, total,
    change: totals[1] - totals[0],
    changePercent: totals[0] ? (totals[1] - totals[0]) / totals[0] * 100 : null,
    option: {
      ...options,
      textStyle: { fontFamily: 'Roboto, Helvetica Neue, sans-serif', ...options.textStyle },
      title: { text: config.comparison.unit, left: 8, top: 0, textStyle: { fontSize: 11, fontWeight: 'normal', color: '#606060' }, ...options.title },
      aria: { enabled: true, label: { description: displayRows.map((row: any) => `${row.label}: ${config.series.map((series: any, index: number) => `${series.label} ${money(row.amounts[index])}`).join(', ')}`).join('. ') } },
      tooltip: {
        trigger: 'axis', renderMode: 'richText', confine: true, axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
          const row = displayRows[params[0]?.dataIndex];
          if (!row) return '';
          return [row.label, ...params.map(param => `${param.seriesName}: ${money(Number(param.value))}`), `${config.labels.change}: ${money(row.amounts[1] - row.amounts[0])}`].join('\n');
        }, ...options.tooltip,
      },
      legend: { bottom: 0, type: 'scroll', selectedMode: true, textStyle: { fontSize: 11 }, ...options.legend, data: config.series.map((series: any) => series.label) },
      grid: { left: 12, right: 36, top: 28, bottom: 64, containLabel: true, ...options.grid },
      xAxis: { type: 'value', min: 0, axisLabel: { fontSize: 11 }, splitLine: { lineStyle: { color: '#eceff2' } }, ...options.xAxis },
      yAxis: {
        type: 'category', inverse: true, axisTick: { show: false }, axisLine: { show: false }, ...options.yAxis,
        data: displayRows.map((row: any) => row.label),
        axisLabel: { fontSize: 11, width: config.comparison.categoryLabelWidth, overflow: 'break', ...options.yAxis?.axisLabel },
      },
      series: config.series.map((series: any, index: number) => ({
        name: series.label, type: 'bar', barMaxWidth: config.comparison.barMaxWidth, barGap: '20%',
        itemStyle: { color: series.color }, emphasis: { focus: 'series' },
        data: displayRows.map((row: any) => ({
          value: row.amounts[index], itemStyle: { color: row.id === 'total' ? series.totalColor || series.color : series.color },
          label: {
            show: config.comparison.showValues,
            position: row.amounts[index] < config.comparison.outsideLabelBelow ? 'right' : 'insideRight',
            color: row.amounts[index] < config.comparison.outsideLabelBelow ? '#454f59' : '#fff',
            fontSize: config.comparison.valueFontSize, formatter: () => money(row.amounts[index]),
          },
        })),
      })),
    },
  };
}
